import { SelectionModel } from '@angular/cdk/collections'
import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  ViewChild,
} from '@angular/core'
import { UntypedFormControl } from '@angular/forms'
import { MatPaginator, PageEvent } from '@angular/material/paginator'
import { MatProgressBar } from '@angular/material/progress-bar'
import { MatSort, Sort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'
import { ClinicService, PagedParams, PagedRestResponse } from '@mmx/shared'
import { get, isArray, orderBy, sortBy } from 'lodash'
import { Observable, Subject } from 'rxjs'
import { debounceTime, tap } from 'rxjs/operators'

import { AdvancedTablePageEvent } from './advanced-table-page-event'
import { CellTemplateDirective } from './cell-template.directive'
import { ColumnDef } from './column-definition'

export interface AdvancedTableOptions<RowModel = any> {
  title?: string
  sortable?: boolean
  paging?: boolean
  autoTriggerFirstPage?: boolean
  defaultPageSize?: number
  search?: boolean
  searchDebounce?: number
  filtering?: boolean
  selection?: boolean
  loadingText?: string
  emptyDataText?: string
  rowClickable?: (row: RowModel, $event: MouseEvent) => void
  actions?: boolean
  actionsLabel?: string
  hiddenColumns?: string[]
  stickyLastColumn?: boolean
  rowClass?: (row: RowModel) => string | string[] | { [key: string]: boolean }
  sortActive?: string
  sortDirection?: 'asc' | 'desc'
  styles?: {
    stickyHeader?: boolean
    denseDisplay?: boolean
    selectedRowClass?: string
    rowClickableClass?: string
    tableHeaderClass?: string
    tableRowHeight?: string
  }
}

function normalizeValue(value: any) {
  value =
    value == null || value === undefined ? '' : String(value).replace(/,/g, '')
  value = isNaN(parseFloat(value))
    ? String(value).trim()
    : parseFloat(value).toFixed(2)
  return value
}

function valueTransform<R>(column: ColumnDef<R>, colValue: any) {
  colValue = normalizeValue(colValue)
  return column.propertyType === 'Date' || column.propertyType === 'DateTime'
    ? new Date(colValue.toString())
    : column.propertyType === 'Number'
      ? Number(colValue)
      : colValue
}

@Component({
  selector: 'mmx-advanced-table',
  templateUrl: './advanced-table.component.html',
  styleUrls: ['./advanced-table.component.scss'],
  standalone: false,
})
export class AdvancedTableComponent<RowModel>
implements AfterContentInit, OnChanges, OnInit
{
  public static defaultOptions: AdvancedTableOptions<any> = {
    sortable: true,
    paging: true,
    autoTriggerFirstPage: true,
    search: true,
    searchDebounce: 300,
    defaultPageSize: 15,
    filtering: false,
    selection: false,
    loadingText: 'common.please-wait',
    emptyDataText: 'common.no-data',
    rowClickable: null,
    actions: false,
    actionsLabel: 'Actions',
    hiddenColumns: [],
    styles: {
      stickyHeader: false,
      denseDisplay: false,
      selectedRowClass: 'ngx-mat-selected',
      tableHeaderClass: 'ngx-thead',
      tableRowHeight: '1rem',
    },
  }

  @Input() selection = new SelectionModel(true, [])
  // @Input() data: RowModel[] = []
  @Input() dataSource: MatTableDataSource<RowModel> = new MatTableDataSource([])
  @Input() total: number | undefined
  @Input() matSortActive: string
  @Input() columns: ColumnDef<RowModel>[] = []
  @Input() displayedColumns: string[]
  @Input() loading = false
  @Output() page: EventEmitter<AdvancedTablePageEvent> = new EventEmitter()

  /**
   * @description the table options as defined in NgxMatTableOptions
   * see  NgxMatTableOptionsDefaults for default values
   */
  @Input()
  set options(v: Partial<AdvancedTableOptions<RowModel>>) {
    this._options = v
      ? {
        ...AdvancedTableComponent.defaultOptions,
        ...v,
      }
      : AdvancedTableComponent.defaultOptions
  }

  get options(): Partial<AdvancedTableOptions<RowModel>> {
    return this._options || AdvancedTableComponent.defaultOptions
  }

  @ViewChild(MatPaginator) paginator: MatPaginator
  @ViewChild(MatSort) sort: MatSort
  @ViewChild(MatProgressBar, { static: true }) progressBar: MatProgressBar

  showFilters = true
  templates: Partial<CellTemplateDirective>[]
  searchControl: UntypedFormControl
  filter$: Observable<string>
  timezone: string
  get = get

  get empty() {
    return this.dataSource && !this.dataSource.data.length && !this.loading
  }

  readonly selectionColumnName = 'selection'
  readonly actionsColumn = new ColumnDef({
    verboseName: 'Actions',
    key: 'actions',
    order: 999,
  })

  public startAtMap: Map<number, string> = new Map()
  public pageChange: Observable<PagedParams>

  get pageIndex(): number {
    return this.currentPage ? this.currentPage.pageIndex : 0
  }

  get currentPage(): AdvancedTablePageEvent {
    return this._page
  }

  get data(): RowModel[] {
    return this.allData
  }

  private allData: RowModel[] = []
  private pageChangeSubject: Subject<PagedParams> = new Subject()

  @ContentChildren(CellTemplateDirective)
  private _templates: QueryList<CellTemplateDirective>
  // private originalData: RowModel[] = []
  private _options: AdvancedTableOptions<RowModel>
  private _sort: Sort
  private _page: PageEvent

  constructor(public cdr: ChangeDetectorRef, clinicService: ClinicService) {
    this.pageChange = this.pageChangeSubject.asObservable()
    this.timezone = clinicService.clinic?.settings?.defaultTimezone ?? 'local'
  }

  ngAfterContentInit() {
    this.templates = this._templates.toArray().map((tmp) => ({
      template: tmp.template,
      name: tmp.name,
    }))

    this.buildColumns()

    if (this.options.paging && this.options.autoTriggerFirstPage) {
      this.getFirstPage()
    }
  }

  ngOnChanges() {
    // if (changes && changes.data) {
    //   // const source = this.data || []
    //   // this.dataSource.data = source

    //   // if (!this.originalData.length) {
    //   //   this.originalData = cloneDeep(source)
    //   // }
    // }

    this.buildColumns()
  }

  ngOnInit() {
    this.searchControl = new UntypedFormControl()
    this.filter$ = this.searchControl.valueChanges.pipe(
      debounceTime(this.options.searchDebounce),
      tap((val) => {
        if (this.options.search) {
          this.applyFilter(val)
        }
      }),
    )
  }

  applyFilter(filterValue: any) {
    filterValue = normalizeValue(filterValue)
    this.dataSource.filterPredicate = (object, filter) => {
      return this.columns
        .map((column) => {
          const columnType = column.propertyType
          let columnValue = column.propertyAccessor
            ? column.propertyAccessor(object[column.key], object)
            : object[column.key]

          switch (columnType) {
            case 'Date':
            case 'DateTime':
              const isDate = !isNaN(new Date(filter).getTime())
              return (
                isDate ? new Date(filter).toISOString() : String(filter)
              ).includes(
                isDate ? new Date(columnValue).toISOString() : columnValue,
              )
            case 'Number':
              columnValue = isNaN(parseFloat(columnValue))
                ? String(columnValue).trim()
                : parseFloat(columnValue).toFixed(2)
              return String(columnValue).includes(filter)
            case 'String':
              return String(columnValue)
                .trim()
                .toLowerCase()
                .includes(String(filter).toLowerCase().trim())
            case 'Object':
              return columnValue === filter
          }
        })
        .find((ok) => ok)
    }
    this.dataSource.filter = filterValue
  }

  notInHidden(item: ColumnDef<RowModel>) {
    return !this.options.hiddenColumns.includes(item.key)
  }

  templateForColumn(column: ColumnDef<RowModel>): Partial<CellTemplateDirective> | undefined {
    return this.templateByName(column.key)
  }

  templateByName(templateName: string): Partial<CellTemplateDirective> | undefined {
    return this.templates.find(template => template.name === templateName)
  }

  sortData(sort: Sort) {
    // if paging is disabled, or we have all the data, we can sort client-side
    if (this.options.paging && this.allData.length !== this.total) {
      this._sort = sort
      this.paginator.pageIndex = 0
      this.handlePageChange(
        {
          pageIndex: this.paginator.pageIndex,
          pageSize: this.paginator.pageSize, // will be overridden for us if forcingPageSize is set
          length: null,
        },
        true,
      )
    } else {
      const isAsc = sort.direction === 'asc'
      const column = this.columns.find((c) => c.key === sort.active)
      const direction = isAsc ? 'asc' : 'desc'
      if (!sort.active || sort.direction === '') {
        this.dataSource.data = this.allData.slice()
        return
      }

      let searchPredicate: (val: any) => any

      if (column.sortByAccessor) {
        searchPredicate = (instance) =>
          valueTransform(column, column.sortByAccessor(instance[instance]))
      } else if (column.propertyAccessor) {
        searchPredicate = (instance) =>
          valueTransform(
            column,
            column.propertyAccessor(instance[column.key], instance),
          )
      } else {
        searchPredicate = (instance) =>
          valueTransform(column, instance[sort.active])
      }

      this.dataSource.data = orderBy(
        this.allData,
        [searchPredicate],
        [direction],
      )
      this.paginator.length = this.total
    }
  }

  setLoading(loading: boolean) {
    this.loading = loading
    this.cdr.markForCheck()
  }

  getFirstPage(filters: any = {}) {
    this.setLoading(true)
    filters.total = true
    filters.pageIndex = 0

    // if we are calling get page after the user filters, we want to keep the page size
    filters.pageSize = this.currentPage
      ? this.currentPage.pageSize
      : this.options.defaultPageSize
    this.pageChangeSubject.next(filters)
  }

  triggerGetPage() {
    const params: PagedParams = {}

    // inject the start at
    const previousPageIndex =
      this.currentPage && this.currentPage.previousPageIndex != null
        ? this.currentPage.previousPageIndex
        : 0
    if (this.startAtMap.has(previousPageIndex)) {
      params.startAt = this.startAtMap.get(previousPageIndex)
    }

    // tell the server it doesn't need to return the total count if we have it
    if (this.total != null) {
      params.total = false
    }

    // pass over the sort column
    if (this.currentPage) {
      params.pageIndex = this.currentPage.pageIndex
      params.pageSize = this.currentPage.pageSize

      if (this.currentPage.sortBy) {
        params.sortBy = this.currentPage.sortBy
        params.sortDir = this.currentPage.sortDir
      }
    }

    this.setLoading(true)
    this.pageChangeSubject.next(params)
  }

  appendData(response: PagedRestResponse<RowModel[]>) {
    const data = response.data
    this.allData = this.allData.concat(data)

    if (response.startAt) {
      this.startAtMap.set(this.pageIndex, response.startAt)
    } else {
      this.startAtMap.set(this.pageIndex, 'IGNORE')
    }

    if (response.total != null) {
      this.total = response.total
    }

    if (this.paginator && data.length !== this.paginator.pageSize) {
      const startIndex = this.paginator.pageIndex * this.paginator.pageSize
      const endIndex = startIndex + this.paginator.pageSize
      const slice = this.allData.slice(startIndex, endIndex)
      this.dataSource.data = slice
    } else {
      this.dataSource.data = data
    }

    this.setLoading(false)
    this.cdr.detectChanges()
  }

  insertRow(...rows: RowModel[]) {
    this.total = this.total += rows.length
    this.dataSource.data = this.dataSource.data.concat(rows)
  }

  deleteRow(row: RowModel) {
    const index = this.dataSource.data.indexOf(row)
    this.dataSource.data.splice(index, 1)
    this.dataSource._updateChangeSubscription()
  }

  rowClicked(row: RowModel, event: MouseEvent) {
    if (this.options.selection) {
      this.selection.toggle(row)
    } else if (this.options.rowClickable != null) {
      this.options.rowClickable(row, event)
    }
  }

  reset() {
    this.startAtMap = new Map()
    this.total = null
    this.allData = []
    this.dataSource.data = []
    if (this.paginator) {
      this.paginator.firstPage()
    }
  }

  handlePageChange(page: PageEvent, sortChanged = false) {
    const pageSizeChanged =
      (this._page && this._page.pageSize !== page.pageSize) ||
      (!this._page && page.pageSize !== this.options.defaultPageSize)

    const e: AdvancedTablePageEvent = {
      ...page,
      pageSizeChanged,
    }

    if (this._sort) {
      e.sortChanged = sortChanged
      e.sortBy = this._sort.active || this.matSortActive
      e.sortDir = this._sort.direction || 'desc'
      this._sort = null
    } else if (this.sort) {
      e.sortChanged = false
      e.sortBy = this.sort.active
      e.sortDir = this.sort.direction
    }

    this._page = e

    if (!this.loading && this.dataSource.data.length !== this.total) {
      if (e.sortChanged || e.pageSizeChanged) {
        this.reset()
        this.triggerGetPage()
      } else {
        const startIndex = e.pageIndex * e.pageSize
        const endIndex = startIndex + e.pageSize
        const slice = this.allData.slice(startIndex, endIndex)
        const startAt: string | undefined = this.startAtMap.get(e.pageIndex)

        // if we have all the data we need already, do not request it from the server
        // but expect the last page to not always have a full page of data
        if (slice.length === e.pageSize || startAt === 'IGNORE') {
          // this.pageOfDataSubject.next(slice)
          this.dataSource.data = slice
        } else {
          this.triggerGetPage()
        }
      }

      this.page.emit(e)
    }
  }

  formatArray(value: string | string[] | number | number[]) {
    return isArray(value) ? value.join(', ') : value
  }

  private buildColumns() {
    if (!this.columns) {
      return
    }

    this.sortColumns()

    if (this.options.actions) {
      if (!this.columns.find((col) => col.key === this.actionsColumn.key)) {
        this.columns = [
          ...this.columns,
          {
            ...this.actionsColumn,
            verboseName:
              this.options.actionsLabel || this.actionsColumn.verboseName,
          },
        ]
      }
    }

    this.displayedColumns = this.columns
      .filter(
        (col) => col.visible && !this.options.hiddenColumns.includes(col.key),
      )
      .map((col) => col.key)

    if (
      this.options.selection &&
      !this.displayedColumns.includes(this.selectionColumnName)
    ) {
      this.displayedColumns = [
        this.selectionColumnName,
        ...this.displayedColumns,
      ]
    }
    if (
      this.dataSource.sort &&
      this.dataSource.data.length &&
      this.columns.length > 0
    ) {
      const sortByColumn =
        this.columns.find((col) => !!col.sortBy) || this.columns[0]

      this.dataSource.sort.sort({
        id: sortByColumn.key,
        start: sortByColumn.sortDirection,
        disableClear: false,
      })
    }
  }

  private sortColumns() {
    this.columns = sortBy(this.columns, ['order'])
  }
}
